import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { openLinkInBlank } from 'utils/utils';
import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import usePrintFile from 'modules/FileManagerV2/hooks/ui/usePrintFile';
import useFileDownload from 'modules/FileManagerV2/hooks/ui/useFileDownload';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useFileNavigation } from 'modules/FileManagerV2/hooks/ui/useFileNavigation';
import { queryKeys } from 'hooks/config/queryClient';
import { useQueryClient } from '@tanstack/react-query';

const DOC_TYPE_VAL = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const PRINT_FILE_FORMATS = ['image/png', 'application/pdf', DOC_TYPE_VAL];

/**
 * Encapsulates all menu-item logic for the grid/list MenuIcon.
 * @param {Object} params
 * @param {Function} params.onDownloadComplete - Callback invoked after a file download.
 * @returns {{ selectFile: Function, getItemsOption: Function, deletePermission: boolean }}
 */
export function useMenuIconItems({ onDownloadComplete }) {
  const {
    deletePermission,
    handleBreadCrumbs,
    getFiles,
    currentView,
    setSelectedItem,
    modals,
    onFilterChange,
    setVersionFileUpload,
    pageLimit,
    isGlobalFileManager,
    caseDetails,
    isClientFileManager,
    setSelectedRows,
  } = useFileManagerContext();

  const {
    setShowRenameModal,
    setMoveItemModal,
    setShowDeleteModal,
    setShowFileUploadModal,
    setShowShareFileModal,
    setShowSignatureModal,
    setShowSendFaxModal,
  } = modals;

  const { checkSubPermission } = useUserDetailsContext();
  const { pdfTronPrintFile } = usePrintFile();
  const { downloadFile } = useFileDownload();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { setQuery } = useFileNavigation({
    handleBreadCrumbs,
    onFilterChange,
  });

  const selectFile = useCallback(
    (file) => {
      if (file.entry_type === 'folder') {
        handleBreadCrumbs(file);
        const obj = {
          case_id: isGlobalFileManager || isClientFileManager ? '' : caseDetails.case_id,
          selectedFolder: file.object_id,
          selectedType: '',
          tag: '',
          query: '',
          folder_id: '',
        };
        // getFiles(obj, currentView, pageLimit);
        queryClient.invalidateQueries({ queryKey: queryKeys.fileManager.list(obj.case_id) });
        onFilterChange({ target: { name: 'selectedFolder' }, value: file.object_id });
      } else {
        setQuery(file);
      }
    },
    [isGlobalFileManager, isClientFileManager, caseDetails, currentView, pageLimit, handleBreadCrumbs, getFiles, onFilterChange, setQuery]
  );

  const getItemsOption = useCallback(
    (rowData) => {
      if (isGlobalFileManager) {
        return [
          { label: 'Open', command: () => selectFile(rowData), show: true },
          {
            label: 'Rename',
            command: () => {
              setShowRenameModal(true);
              setSelectedItem(rowData);
            },
            show: rowData?.is_virtual ? false : true,
          },
          {
            label: 'Delete',
            command: () => {
              setShowDeleteModal(true);
              setSelectedItem(rowData);
            },
            show: rowData?.is_virtual ? false : true,
          },
          {
            label: 'Move',
            command: () => {
              setMoveItemModal(true);
              setSelectedItem(rowData);
            },
            show: rowData?.is_virtual ? false : true,
          },
        ];
      }

      const uploadNewVersionObj = {
        label: 'Upload New Version',
        command: () => {
          setShowFileUploadModal(true);
          setSelectedItem(rowData);
          setVersionFileUpload(rowData);
        },
        show: rowData?.is_virtual ? false : true,
      };

      const shareFileObj = {
        label: 'Share',
        command: () => {
          setShowShareFileModal(true);
          setSelectedRows([rowData]);
        },
        show: true,
      };

      const downloadObj = {
        label: 'Download',
        command: () => downloadFile(rowData, onDownloadComplete),
        show: true,
      };

      const printObj = {
        label: 'Print',
        command: () => pdfTronPrintFile(rowData),
        show: Boolean(rowData?.file_type && PRINT_FILE_FORMATS.includes(rowData?.file_type?.toLowerCase())),
      };

      const movObj = {
        label: 'Move',
        command: () => {
          setMoveItemModal(true);
          setSelectedItem(rowData);
        },
        show: rowData?.is_virtual ? false : true,
      };

      const withSignature = [
        { label: 'Open', command: () => selectFile(rowData), show: true },
        {
          label: 'Rename',
          command: () => {
            setShowRenameModal(true);
            setSelectedItem(rowData);
          },
          show: rowData?.is_virtual ? false : true,
        },
        {
          label: 'Delete',
          command: () => {
            setShowDeleteModal(true);
            setSelectedItem(rowData);
          },
          show: rowData?.is_virtual ? false : true,
        },
        {
          label: 'Request Signature',
          command: () => {
            setSelectedItem(rowData);
            setShowSignatureModal(true);
          },
          show: true,
        },
      ];

      const withOutSignature = [
        { label: 'Open', command: () => selectFile(rowData), show: true },
        {
          label: 'Rename',
          command: () => {
            setShowRenameModal(true);
            setSelectedItem(rowData);
          },
          show: rowData?.is_virtual ? false : true,
        },
        {
          label: 'Delete',
          command: () => {
            setShowDeleteModal(true);
            setSelectedItem(rowData);
          },
          show: rowData?.is_virtual ? false : true,
        },
      ];

      if (rowData?.entry_type !== 'folder') {
        withSignature.push(uploadNewVersionObj);
        withOutSignature.push(uploadNewVersionObj);

        if (currentView === 'grid') {
          withSignature.push(printObj, downloadObj);
          withOutSignature.push(printObj, downloadObj);
        }
      }

      if (rowData.case_id && rowData.case_id !== 'undefined') {
        withSignature.push(shareFileObj);
        withOutSignature.push(shareFileObj);
      }

      if (!isGlobalFileManager) {
        withSignature.push(movObj);
        withOutSignature.push(movObj);
      }

      const fileType = rowData?.file_type?.toLowerCase();

      if (fileType === 'docx' || fileType === DOC_TYPE_VAL) {
        const editMenu = {
          label: 'Edit',
          show: true,
          command: () => {
            localStorage.setItem('file_url', rowData.file_path);
            localStorage.setItem('version_id', rowData.version_id);
            localStorage.setItem('file_name', rowData.display_name.replace('.docx', ''));
            localStorage.setItem('file_size', rowData.size);
            localStorage.setItem('file_type', rowData.file_type);
            localStorage.setItem('pk', rowData.pk);
            localStorage.setItem('sk', rowData.sk);
            localStorage.setItem('mode', 'edit');

            const pathname = '/templates/builder';
            const state = {
              type: 'edit',
              save_as: 'file',
              case_id: rowData?.case_id,
              from: history.location?.pathname,
              caseName: rowData?.case_name,
            };
            if (!openLinkInBlank(pathname, state)) {
              history.push({ pathname, state });
            }
          },
        };
        withSignature.push(editMenu);
        withOutSignature.push(editMenu);
      }

      if (fileType === 'pdf' || fileType === 'application/pdf') {
        const faxMenu = {
          label: 'Send as fax',
          show: true,
          command: () => {
            setShowSendFaxModal(true);
            setSelectedItem(rowData);
          },
        };
        withSignature.push(faxMenu);
        withOutSignature.push(faxMenu);
      }

      try {
        const hasSupportedType = rowData?.file_type && (rowData.file_type === 'application/pdf' || rowData.file_type === DOC_TYPE_VAL);
        return hasSupportedType && checkSubPermission('e_signature') ? withSignature : withOutSignature;
      } catch {
        return withSignature;
      }
    },
    [
      isGlobalFileManager,
      currentView,
      checkSubPermission,
      setShowRenameModal,
      setSelectedItem,
      setShowDeleteModal,
      setMoveItemModal,
      setShowFileUploadModal,
      setVersionFileUpload,
      setShowShareFileModal,
      setSelectedRows,
      setShowSignatureModal,
      setShowSendFaxModal,
      downloadFile,
      onDownloadComplete,
      pdfTronPrintFile,
      selectFile,
      history,
    ]
  );

  return { selectFile, getItemsOption, deletePermission };
}
